import type { CSSProperties } from "react";
import { PROP_COLOR_KEYS, isPropColorKey, propColorRgba, type PropColorKey } from "@match/shared";
import { usePropFirms } from "@/lib/queries";

/*
 * Il colore tenue di una prop firm: serve solo a distinguere una prop dall'altra.
 * In database sta la chiave della tavolozza (`PropFirm.color`); qui si decide
 * quanto si vede. Tre dosi, tutte basse: il velo di sfondo appena percettibile,
 * la striscia a sinistra un po' piu' presente ma sobria, il pallino piu' pieno
 * perche' e' piccolo.
 */
const VEIL = 0.05;
const STRIP = 0.5;
const DOT = 0.8;

/**
 * La chiave di ogni firm. Quelle senza colore (null: nate prima del colore e non
 * ancora riempite da `db:fill-prop-colors`) prendono la prima tinta libera, in
 * ordine, senza mai dare errore.
 */
export function resolvePropColors(firms: readonly { id: string; color?: string | null }[]): Map<string, PropColorKey> {
  const out = new Map<string, PropColorKey>();
  const prese = new Set<PropColorKey>();
  for (const f of firms) {
    if (isPropColorKey(f.color)) {
      out.set(f.id, f.color);
      prese.add(f.color);
    }
  }
  for (const f of firms) {
    if (out.has(f.id)) continue;
    const libera = PROP_COLOR_KEYS.find((k) => !prese.has(k)) ?? PROP_COLOR_KEYS[0]!;
    prese.add(libera);
    out.set(f.id, libera);
  }
  return out;
}

/** `(firmId, colore proprio?) → chiave`: per chi ha solo un id (la card di Operazione). */
export function usePropColor(): (firmId: string | null | undefined, own?: string | null) => PropColorKey {
  const firms = usePropFirms();
  const map = resolvePropColors(firms.data ?? []);
  return (firmId, own) => (isPropColorKey(own) ? own : (firmId ? map.get(firmId) : undefined) ?? PROP_COLOR_KEYS[0]!);
}

/** Lo stile della card di una prop: striscia sottile a sinistra e velo di sfondo, entrambi tenui. */
export function propCardStyle(key: PropColorKey): CSSProperties {
  const velo = propColorRgba(key, VEIL);
  return { boxShadow: `inset 3px 0 0 ${propColorRgba(key, STRIP)}`, backgroundImage: `linear-gradient(${velo}, ${velo})` };
}

/** Solo la striscia a sinistra (le righe della pagina Prop firm). */
export function propStripStyle(key: PropColorKey): CSSProperties {
  return { boxShadow: `inset 3px 0 0 ${propColorRgba(key, STRIP)}` };
}

export function PropColorDot({ color, className = "h-2 w-2" }: { color: PropColorKey; className?: string }) {
  return <span className={`inline-block shrink-0 rounded-full ${className}`} style={{ backgroundColor: propColorRgba(color, DOT) }} aria-hidden />;
}

/**
 * La fila dei dieci quadratini. Nessun campo di testo: si sceglie con un clic,
 * e la scelta e' di chi la riceve (`onPick`).
 */
export function PropColorPicker({ value, onPick, disabled }: { value: PropColorKey; onPick: (key: PropColorKey) => void; disabled?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Colore della prop firm" className="flex flex-wrap items-center gap-2">
      {PROP_COLOR_KEYS.map((k) => (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={value === k}
          aria-label={k}
          title={k}
          disabled={disabled}
          onClick={() => onPick(k)}
          className={`h-6 w-6 rounded border ${value === k ? "border-ink" : "border-transparent hover:border-muted"} disabled:opacity-50`}
          style={{ backgroundColor: propColorRgba(k, DOT) }}
        />
      ))}
    </div>
  );
}
